/**
 * Plan geometry for a ceiling-hosted rectangular opening.
 *
 * The perimeter coordinates are the room's INTERIOR wall faces (ADR 0020), so
 * each location is measured from the selected wall line along its inward
 * normal. Reference 1 owns the width axis; Reference 2 owns the length axis.
 * The length axis is kept perpendicular to the width axis and pointed toward
 * the room side of Reference 2, so the physical opening always stays square.
 */

const EPS = 1e-9;
const DEFAULT_LADDER_CEILING_INSET_UNITS = 6;
const DEFAULT_LADDER_SIDE_CLEARANCE_UNITS = 3;
const DEFAULT_OPENING_HEIGHT_UNITS = 1;

function unit(dx, dy) {
  const length = Math.hypot(dx, dy);
  return length > EPS ? { x: dx / length, y: dy / length } : null;
}

function add(a, b, amount = 1) {
  return { x: a.x + b.x * amount, y: a.y + b.y * amount };
}

function cleanVector(vector) {
  return { x: vector.x + 0, y: vector.y + 0 };
}

function dot(a, b) {
  return a.x * b.x + a.y * b.y;
}

function intersection(pointA, directionA, pointB, directionB) {
  const denominator = directionA.x * directionB.y - directionA.y * directionB.x;
  if (Math.abs(denominator) <= EPS) return null;
  const dx = pointB.x - pointA.x;
  const dy = pointB.y - pointA.y;
  const distanceA = (dx * directionB.y - dy * directionB.x) / denominator;
  return add(pointA, directionA, distanceA);
}

function chainWinding(chain) {
  let sum = 0;
  for (let index = 0; index < chain.length; index++) {
    const a = chain[index].start;
    const b = chain[(index + 1) % chain.length].start;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum >= 0 ? 1 : -1;
}

function wallAxes(wall, winding) {
  const direction = unit(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  if (!direction) return null;
  return {
    direction,
    inward: { x: -direction.y * winding, y: direction.x * winding },
  };
}

/**
 * Returns plan-space corners and ladder endpoints, or null when the references
 * cannot establish two coordinates (missing/degenerate/parallel walls).
 */
export function ceilingOpeningGeometry(chain, opening, {
  pxPerUnit = 1,
  ladderCeilingInsetUnits = DEFAULT_LADDER_CEILING_INSET_UNITS,
  ladderSideClearanceUnits = DEFAULT_LADDER_SIDE_CLEARANCE_UNITS,
} = {}) {
  if (!Array.isArray(chain) || chain.length < 2 || !opening || !(pxPerUnit > 0)) return null;
  const [widthWallId, lengthWallId] = opening.referenceWallIds ?? [];
  const widthWall = chain.find((wall) => wall.id === widthWallId && (wall.segmentKind ?? 'wall') === 'wall');
  const lengthWall = chain.find((wall) => wall.id === lengthWallId && (wall.segmentKind ?? 'wall') === 'wall');
  if (!widthWall || !lengthWall || widthWall === lengthWall) return null;

  const values = [opening.xUnits, opening.yUnits, opening.widthUnits, opening.lengthUnits];
  if (!values.every(Number.isFinite) || opening.xUnits < 0 || opening.yUnits < 0
    || opening.widthUnits <= 0 || opening.lengthUnits <= 0) return null;

  const winding = chainWinding(chain);
  const widthRef = wallAxes(widthWall, winding);
  const lengthRef = wallAxes(lengthWall, winding);
  if (!widthRef || !lengthRef) return null;

  // Each offset line starts at the selected INTERIOR wall face and moves into
  // the room. Their intersection is the near corner of the opening.
  const widthOffsetLine = add(widthWall.start, widthRef.inward, opening.xUnits * pxPerUnit);
  const lengthOffsetLine = add(lengthWall.start, lengthRef.inward, opening.yUnits * pxPerUnit);
  const referencePoint = intersection(widthOffsetLine, widthRef.direction, lengthOffsetLine, lengthRef.direction);
  if (!referencePoint) return null;

  const widthAxis = cleanVector(widthRef.inward);
  const perpendicular = { x: -widthAxis.y, y: widthAxis.x };
  const lengthAxis = cleanVector(perpendicular.x * lengthRef.inward.x + perpendicular.y * lengthRef.inward.y >= 0
    ? perpendicular
    : { x: -perpendicular.x, y: -perpendicular.y });
  const width = opening.widthUnits * pxPerUnit;
  const length = opening.lengthUnits * pxPerUnit;
  // Rectangular openings are located to their near corner. A circular fan is
  // located to its CENTER—the two wall readings a field measurer can reproduce
  // unambiguously—then expanded by its radius on both axes.
  const near = opening.kind === 'ceiling_fan'
    ? add(add(referencePoint, widthAxis, -width / 2), lengthAxis, -length / 2)
    : referencePoint;
  const nearWidth = add(near, widthAxis, width);
  const far = add(near, lengthAxis, length);
  const farWidth = add(nearWidth, lengthAxis, length);
  const center = {
    x: (near.x + nearWidth.x + farWidth.x + far.x) / 4,
    y: (near.y + nearWidth.y + farWidth.y + far.y) / 4,
  };

  let ladder = null;
  if (opening.kind === 'attic_stairs' && Number.isFinite(opening.ladderFloorOffsetUnits)
    && opening.ladderFloorOffsetUnits >= 0) {
    const widthCenter = width / 2;
    const insetUnits = Number.isFinite(ladderCeilingInsetUnits) && ladderCeilingInsetUnits >= 0
      ? ladderCeilingInsetUnits : DEFAULT_LADDER_CEILING_INSET_UNITS;
    const ceilingInset = Math.min(insetUnits, opening.lengthUnits) * pxPerUnit;
    const ceiling = add(add(near, widthAxis, widthCenter), lengthAxis, ceilingInset);
    const floorDelta = (opening.ladderFloorOffsetUnits - opening.yUnits) * pxPerUnit;
    const floor = add(add(near, widthAxis, widthCenter), lengthAxis, floorDelta);
    const clearanceUnits = Number.isFinite(ladderSideClearanceUnits) && ladderSideClearanceUnits >= 0
      ? ladderSideClearanceUnits : DEFAULT_LADDER_SIDE_CLEARANCE_UNITS;
    const effectiveClearanceUnits = Math.min(clearanceUnits, opening.widthUnits / 2);
    const clearance = effectiveClearanceUnits * pxPerUnit;
    const usableWidthUnits = Math.max(0, opening.widthUnits - (2 * effectiveClearanceUnits));
    const footprint = usableWidthUnits > EPS ? [
      add(add(near, widthAxis, clearance), lengthAxis, ceilingInset),
      add(add(near, widthAxis, width - clearance), lengthAxis, ceilingInset),
      add(add(near, widthAxis, width - clearance), lengthAxis, floorDelta),
      add(add(near, widthAxis, clearance), lengthAxis, floorDelta),
    ] : null;
    ladder = {
      start: ceiling, end: floor, ceiling, floor,
      ceilingInsetUnits: insetUnits,
      sideClearanceUnits: effectiveClearanceUnits,
      widthUnits: usableWidthUnits,
      lengthUnits: Math.abs((opening.ladderFloorOffsetUnits - opening.yUnits) - Math.min(insetUnits, opening.lengthUnits)),
      footprint,
    };
  } else if (opening.kind === 'attic_stairs' && Number.isFinite(opening.ladderProjectionUnits)
    && opening.ladderProjectionUnits > 0) {
    // Compatibility for records created before the floor point was measured
    // from Reference 2. New edits never write this ambiguous representation.
    const projection = opening.ladderProjectionUnits * pxPerUnit;
    const start = { x: (far.x + farWidth.x) / 2, y: (far.y + farWidth.y) / 2 };
    const clearanceUnits = Number.isFinite(ladderSideClearanceUnits) && ladderSideClearanceUnits >= 0
      ? ladderSideClearanceUnits : DEFAULT_LADDER_SIDE_CLEARANCE_UNITS;
    const effectiveClearanceUnits = Math.min(clearanceUnits, opening.widthUnits / 2);
    const clearance = effectiveClearanceUnits * pxPerUnit;
    const usableWidthUnits = Math.max(0, opening.widthUnits - (2 * effectiveClearanceUnits));
    const footprint = usableWidthUnits > EPS ? [
      add(far, widthAxis, clearance),
      add(far, widthAxis, width - clearance),
      add(add(far, widthAxis, width - clearance), lengthAxis, projection),
      add(add(far, widthAxis, clearance), lengthAxis, projection),
    ] : null;
    ladder = {
      start, end: add(start, lengthAxis, projection),
      sideClearanceUnits: effectiveClearanceUnits,
      widthUnits: usableWidthUnits,
      lengthUnits: opening.ladderProjectionUnits,
      footprint,
    };
  }

  return {
    points: [near, nearWidth, farWidth, far],
    center,
    widthAxis,
    lengthAxis,
    ladder,
  };
}

/**
 * Projects one extended attic stair onto a wall's front elevation.
 *
 * Walls parallel to the stair WIDTH see its two vertical outside edges and
 * true left/right position. Walls parallel to the stair RUN see the side
 * profile from the ceiling contact to the floor base. Perpendicular reference
 * walls therefore produce the two views the field designer expects, and their
 * opposite walls receive the corresponding mirrored view automatically.
 */
export function atticStairElevationForWall(chain, opening, wallId, {
  pxPerUnit = 1,
  ceilingHeightUnits = 118,
  ladderCeilingInsetUnits = DEFAULT_LADDER_CEILING_INSET_UNITS,
  ladderSideClearanceUnits = DEFAULT_LADDER_SIDE_CLEARANCE_UNITS,
  parallelTolerance = 0.985,
} = {}) {
  if (opening?.kind !== 'attic_stairs' || opening.showLadderExtended === false) return null;
  const wall = Array.isArray(chain) ? chain.find((item) => item.id === wallId && (item.segmentKind ?? 'wall') === 'wall') : null;
  if (!wall || !(pxPerUnit > 0) || !(ceilingHeightUnits > 0)) return null;
  const wallDirection = unit(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  if (!wallDirection) return null;
  const geometry = ceilingOpeningGeometry(chain, opening, {
    pxPerUnit, ladderCeilingInsetUnits, ladderSideClearanceUnits,
  });
  if (!geometry?.ladder?.footprint) return null;
  const along = (point) => dot({ x: point.x - wall.start.x, y: point.y - wall.start.y }, wallDirection) / pxPerUnit;
  const widthAlignment = Math.abs(dot(wallDirection, geometry.widthAxis));
  const lengthAlignment = Math.abs(dot(wallDirection, geometry.lengthAxis));

  if (widthAlignment >= parallelTolerance && widthAlignment >= lengthAlignment) {
    const edges = [along(geometry.ladder.footprint[0]), along(geometry.ladder.footprint[1])].sort((a, b) => a - b);
    return {
      kind: 'front', openingId: opening.id ?? null,
      fromUnits: edges[0], toUnits: edges[1],
      widthUnits: edges[1] - edges[0],
      ceilingHeightUnits,
    };
  }
  if (lengthAlignment >= parallelTolerance) {
    return {
      kind: 'side', openingId: opening.id ?? null,
      ceilingAtUnits: along(geometry.ladder.ceiling ?? geometry.ladder.start),
      floorAtUnits: along(geometry.ladder.floor ?? geometry.ladder.end),
      ceilingHeightUnits,
      lengthUnits: geometry.ladder.lengthUnits,
    };
  }
  return null;
}

/**
 * Projects a ceiling opening's shallow frame onto one wall elevation.
 * The plan uses the same stored height for its small extrusion; elevations
 * show the measurable result directly below the ceiling.
 */
export function ceilingOpeningElevationForWall(chain, opening, wallId, {
  pxPerUnit = 1,
  parallelTolerance = 0.985,
} = {}) {
  const wall = Array.isArray(chain) ? chain.find((item) => item.id === wallId && (item.segmentKind ?? 'wall') === 'wall') : null;
  if (!wall || !(pxPerUnit > 0)) return null;
  const wallDirection = unit(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  if (!wallDirection) return null;
  const geometry = ceilingOpeningGeometry(chain, opening, { pxPerUnit });
  if (!geometry) return null;
  const widthAlignment = Math.abs(dot(wallDirection, geometry.widthAxis));
  const lengthAlignment = Math.abs(dot(wallDirection, geometry.lengthAxis));
  if (Math.max(widthAlignment, lengthAlignment) < parallelTolerance) return null;
  const along = (point) => dot({ x: point.x - wall.start.x, y: point.y - wall.start.y }, wallDirection) / pxPerUnit;
  const points = widthAlignment >= lengthAlignment
    ? [geometry.points[0], geometry.points[1]]
    : [geometry.points[0], geometry.points[3]];
  const span = points.map(along).sort((a, b) => a - b);
  const heightUnits = Number.isFinite(opening.heightUnits) && opening.heightUnits > 0
    ? opening.heightUnits : DEFAULT_OPENING_HEIGHT_UNITS;
  return {
    openingId: opening.id ?? null,
    fromUnits: span[0],
    toUnits: span[1],
    heightUnits,
    ...(opening.kind === 'ceiling_fan' ? {
      kind: 'ceiling_fan',
      fromFloorUnits: Number.isFinite(opening.fromFloorUnits) && opening.fromFloorUnits >= 0
        ? opening.fromFloorUnits : null,
    } : {}),
  };
}
